// src/PulsePage.js
//
// Live operations view. Unlike the Dashboard tab (which loads counts once on
// mount), this subscribes to Postgres changes and re-renders as they happen —
// so a booking taken or a payout approved shows up without a refresh.
//
// Motion is doing real work here rather than decorating: a figure only
// animates when its value actually changed, which turns "did something just
// happen?" into something you catch peripherally instead of having to diff
// the screen yourself. Transitions are from the transitions.dev skill
// (number pop-in, panel reveal, success check); every one keeps its
// prefers-reduced-motion guard.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert, Chip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { supabase } from './supabaseClient';
import { brand, ink, radius, statusTone, surface, type as typeScale } from './designTokens';
import DepthCard from './DepthCard';
import { prettyStatus } from './labels';
// ── transitions.dev snippets, scoped to this page ────────────────────────────
const TRANSITION_CSS = `
:root {
  --digit-dur: 500ms; --digit-distance: 8px; --digit-stagger: 70ms; --digit-blur: 2px;
  --digit-ease: cubic-bezier(0.34, 1.45, 0.64, 1); --digit-dir-x: 0; --digit-dir-y: 1;
  --panel-open-dur: 420ms; --panel-close-dur: 300ms; --panel-translate-y: 14px;
  --panel-blur: 4px; --panel-ease: cubic-bezier(0.22, 1, 0.36, 1);
}
@keyframes t-digit-pop-in {
  0% { transform: translate(calc(var(--digit-distance) * var(--digit-dir-x)),
                            calc(var(--digit-distance) * var(--digit-dir-y)));
       opacity: 0; filter: blur(var(--digit-blur)); }
  100% { transform: translate(0,0); opacity: 1; filter: blur(0); }
}
.t-digit-group { display: inline-flex; align-items: baseline; }
.t-digit { display: inline-block; will-change: transform, opacity, filter; }
.t-digit-group.is-animating .t-digit { animation: t-digit-pop-in var(--digit-dur) var(--digit-ease) both; }
.t-digit-group.is-animating .t-digit[data-stagger="1"] { animation-delay: var(--digit-stagger); }
.t-digit-group.is-animating .t-digit[data-stagger="2"] { animation-delay: calc(var(--digit-stagger) * 2); }

.t-panel-slide {
  transform: translateY(var(--panel-translate-y)); opacity: 0; filter: blur(var(--panel-blur));
  pointer-events: none;
  transition: transform var(--panel-close-dur) var(--panel-ease),
              opacity var(--panel-close-dur) var(--panel-ease),
              filter var(--panel-close-dur) var(--panel-ease);
  will-change: transform, opacity, filter;
}
.t-panel-slide[data-open="true"] {
  transform: translateY(0); opacity: 1; filter: blur(0); pointer-events: auto;
  transition: transform var(--panel-open-dur) var(--panel-ease),
              opacity var(--panel-open-dur) var(--panel-ease),
              filter var(--panel-open-dur) var(--panel-ease);
}

@media (prefers-reduced-motion: reduce) {
  .t-digit-group .t-digit { animation: none !important; }
  .t-panel-slide { transition: none !important; }
  .pulse-dot { animation: none !important; }
}

@keyframes pulse-beat { 0%,100% { opacity: 1; } 50% { opacity: 0.25; } }
.pulse-dot { animation: pulse-beat 2s ease-in-out infinite; }
`;

/// Renders a value as individually-animating characters, replaying the
/// pop-in only when the value actually changes — animating on every render
/// would make the page twitch constantly under a realtime subscription.
function AnimatedValue({ value, size = 30, color }) {
  const [animating, setAnimating] = useState(false);
  const previous = useRef(value);

  useEffect(() => {
    if (previous.current === value) return;
    previous.current = value;
    setAnimating(false);
    // Force a reflow so the animation restarts rather than being coalesced.
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setAnimating(true)));
    return () => cancelAnimationFrame(id);
  }, [value]);

  const chars = String(value).split('');
  const staggerStart = Math.max(0, chars.length - 2);

  return (
    <span
      className={`t-digit-group${animating ? ' is-animating' : ''}`}
      style={{ fontSize: size, fontWeight: 500, color: color || ink[900], lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}
    >
      {chars.map((c, i) => (
        <span
          key={`${i}-${c}`}
          className="t-digit"
          data-stagger={i >= staggerStart ? String(i - staggerStart + 1) : undefined}
        >
          {c === ' ' ? ' ' : c}
        </span>
      ))}
    </span>
  );
}

function MetricTile({ label, value, sub, active, onClick }) {
  return (
    <DepthCard active={active} onClick={onClick} sx={{ minWidth: 180 }}>
      <Typography sx={{ color: ink[500], fontSize: typeScale.micro, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1.2 }}>
        {label}
      </Typography>
      <AnimatedValue value={value} />
      {sub && <Typography sx={{ color: ink[300], fontSize: typeScale.micro, mt: 0.8 }}>{sub}</Typography>}
    </DepthCard>
  );
}

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PulsePage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [openPanel, setOpenPanel] = useState(null);
  const [data, setData] = useState({
    bookingsToday: 0, pendingBookings: 0, gmvMonth: 0, pendingPayoutTotal: 0,
    activeStylists: 0, recentBookings: [], pendingPayouts: [],
  });

  const load = useCallback(async () => {
    setError(null);
    try {
      const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
      const startOfMonth = new Date(); startOfMonth.setDate(1); startOfMonth.setHours(0, 0, 0, 0);

      const [bookingsRes, payoutsRes, stylistsRes] = await Promise.all([
        supabase.from('bookings')
          .select('id, service_title, price, status, payment_completed, created_at, stylists_phone_number')
          .order('created_at', { ascending: false }).limit(200),
        supabase.from('payout_requests')
          .select('id, total_amount, status, stylist_phone_number, created_at')
          .order('created_at', { ascending: false }).limit(50),
        supabase.from('Profiles').select('id', { count: 'exact', head: true }).eq('user_type', 'Stylist'),
      ]);

      if (bookingsRes.error) throw bookingsRes.error;
      const bookings = bookingsRes.data || [];
      const payouts = payoutsRes.data || [];

      setData({
        bookingsToday: bookings.filter((b) => new Date(b.created_at) >= startOfDay).length,
        pendingBookings: bookings.filter((b) => String(b.status).toLowerCase() === 'requested').length,
        // Only completed payments count as realised revenue — an authorised
        // hold that was never captured is not money.
        gmvMonth: bookings
          .filter((b) => b.payment_completed && new Date(b.created_at) >= startOfMonth)
          .reduce((s, b) => s + parseFloat(b.price || 0), 0),
        pendingPayoutTotal: payouts
          .filter((p) => String(p.status).toLowerCase() === 'pending')
          .reduce((s, p) => s + parseFloat(p.total_amount || 0), 0),
        activeStylists: stylistsRes.count || 0,
        recentBookings: bookings.slice(0, 8),
        pendingPayouts: payouts.filter((p) => String(p.status).toLowerCase() === 'pending').slice(0, 8),
      });
      setLastUpdate(new Date());
    } catch (err) {
      console.error('PulsePage load failed:', err);
      setError(err.message || 'Could not load live data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Same realtime pattern already used by BookingsPage / PayoutsPage.
    const channel = supabase
      .channel('pulse-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bookings' }, load)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'payout_requests' }, load)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [load]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress /></Box>;
  }

  const togglePanel = (name) => setOpenPanel((cur) => (cur === name ? null : name));

  return (
    <Box sx={{ p: 3 }}>
      <style>{TRANSITION_CSS}</style>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <Typography sx={{ fontSize: typeScale.h1, color: ink[900] }}>Pulse</Typography>
        <Box className="pulse-dot" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: brand.gold }} />
        <Typography sx={{ fontSize: typeScale.micro, color: ink[500], letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Live
        </Typography>
        <Box sx={{ flex: 1 }} />
        {lastUpdate && (
          <Typography sx={{ fontSize: typeScale.micro, color: ink[300] }}>
            updated {lastUpdate.toLocaleTimeString()}
          </Typography>
        )}
        <RefreshIcon
          onClick={load}
          sx={{ cursor: 'pointer', fontSize: 18, color: ink[500], '&:hover': { color: brand.gold } }}
        />
      </Box>
      <Typography sx={{ fontSize: typeScale.small, color: ink[500], mb: 3 }}>
        Figures animate when they change — updates arrive without a refresh.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, alignItems: 'stretch' }}>
        <MetricTile label="Bookings today" value={data.bookingsToday} />
        <MetricTile
          label="Awaiting response" value={data.pendingBookings}
          sub={data.recentBookings.length ? 'tap to review' : undefined}
          active={openPanel === 'bookings'} onClick={() => togglePanel('bookings')}
        />
        <MetricTile label="Captured this month" value={money(data.gmvMonth)} />
        <MetricTile
          label="Pending payouts" value={money(data.pendingPayoutTotal)}
          sub={data.pendingPayouts.length ? `${data.pendingPayouts.length} request(s)` : 'none outstanding'}
          active={openPanel === 'payouts'} onClick={() => togglePanel('payouts')}
        />
        <MetricTile label="Stylists" value={data.activeStylists} />
      </Box>

      <Box sx={{ overflow: 'hidden' }}>
        <Box className="t-panel-slide" data-open={openPanel === 'bookings' ? 'true' : 'false'}
             sx={{ display: openPanel === 'bookings' ? 'block' : 'none' }}>
          <DetailPanel
            title="Recent bookings"
            rows={data.recentBookings.map((b) => ({
              key: b.id,
              primary: b.service_title || 'Booking',
              secondary: new Date(b.created_at).toLocaleString(),
              amount: money(b.price),
              status: b.status,
            }))}
            empty="No bookings yet."
          />
        </Box>

        <Box className="t-panel-slide" data-open={openPanel === 'payouts' ? 'true' : 'false'}
             sx={{ display: openPanel === 'payouts' ? 'block' : 'none' }}>
          <DetailPanel
            title="Payout requests awaiting action"
            rows={data.pendingPayouts.map((p) => ({
              key: p.id,
              primary: p.stylist_phone_number || 'Stylist',
              secondary: new Date(p.created_at).toLocaleString(),
              amount: money(p.total_amount),
              status: p.status,
            }))}
            empty="Nothing waiting — every payout is settled."
          />
        </Box>
      </Box>
    </Box>
  );
}

function DetailPanel({ title, rows, empty }) {
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${surface.line}`, borderRadius: `${radius.lg}px`, overflow: 'hidden' }}>
      <Box sx={{ px: 2.5, py: 1.5, bgcolor: surface.sunken, borderBottom: `1px solid ${surface.line}` }}>
        <Typography sx={{ fontSize: typeScale.micro, letterSpacing: '0.08em', textTransform: 'uppercase', color: ink[500] }}>
          {title}
        </Typography>
      </Box>
      {rows.length === 0 ? (
        <Typography sx={{ p: 2.5, fontSize: typeScale.small, color: ink[500] }}>{empty}</Typography>
      ) : (
        rows.map((r) => {
          const tone = statusTone(r.status);
          return (
            <Box key={r.key} sx={{
              display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.6,
              borderBottom: `1px solid ${surface.line}`, '&:last-child': { borderBottom: 'none' },
              '&:hover': { bgcolor: surface.sunken },
            }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: typeScale.body, color: ink[900] }} noWrap>{r.primary}</Typography>
                <Typography sx={{ fontSize: typeScale.micro, color: ink[300] }}>{r.secondary}</Typography>
              </Box>
              <Typography sx={{ fontSize: typeScale.body, color: ink[700], fontVariantNumeric: 'tabular-nums' }}>
                {r.amount}
              </Typography>
              <Chip label={prettyStatus(r.status) || '—'} size="small"
                    sx={{ color: tone.fg, bgcolor: tone.bg, minWidth: 74 }} />
            </Box>
          );
        })
      )}
    </Paper>
  );
}
